import { useEffect, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  useCustomerShareView,
  type ShareColumn,
  type ShareDirection,
  type ShareTable,
  type ShareTotals,
} from '../../api/customer';
import { BRAND_CSS_VARS, BRAND_NAME, BRAND_SLOGAN } from '../../lib/brand';

/**
 * 客户订单核对页：用户把某客户名下的订单发给**非注册客户的对方**对数、结账。
 *
 * 与订单分享页（`/share/order/:token`）的区别只在读者：那一页是「一张单长什么样」，
 * 这一页是「这批单一共多少钱、还差多少」。所以版式按台账来（一张收入、一张支出），
 * 列集合与合计数都不在这里现算——服务端 `customer-share-view.ts` 从台账列定义里筛出来，
 * 页面只做排布，避免「页面上的数和导出的台账对不上」。
 *
 * 这一页只有**一批单**（2026-10-11 用户裁定：未付款与部分付款合并，不再分档）：
 * 还欠着钱的都在这儿，已结清、0 元与已取消的不进核对单。几百条历史单既撑爆页面也不产生
 * 任何要核对的数，要全部流水走导出。
 *
 * 方向语义（2026-10-11 用户裁定）：收入是「客户该付给分享方」、支出是「分享方该付给客户」，
 * 两个方向的余款各算各的（列名就叫待收余款 / 待付余款），页面不出现任何把收支相加的金额。
 */
const PAGE_TITLE = `客户订单核对单 · ${BRAND_NAME}`;

const CARD_CLASS =
  'bg-white rounded-2xl border border-gray-100 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_-16px_rgba(16,24,40,0.16)]';

/** 节名配色：与客户详情页收支 tab 同色系，收件人一眼分清两张表 */
const SECTION_STYLES: Record<ShareDirection, { text: string; bar: string }> = {
  income: { text: 'text-emerald-600', bar: 'bg-emerald-400' },
  expense: { text: 'text-rose-500', bar: 'bg-rose-400' },
};

/** 这两列的典型值是整句地址 / 活动名，不许换行会把表撑到手机上完全读不了 */
const WRAPPING_HEADERS: ReadonlySet<string> = new Set(['活动名称', '活动地址']);

/**
 * 页面骨架：品牌色带 + 上叠的白卡，与订单分享页同一套色温。
 * 容器比那一页宽（`max-w-4xl`）：十三列的表放在窄卡里只能横向滚，读者会以为内容丢了。
 */
function PageShell({ children }: { children: ReactNode }) {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  return (
    <div className="min-h-screen bg-[var(--brand-page)] pb-10" style={BRAND_CSS_VARS}>
      <div className="bg-gradient-to-b from-[var(--brand-deep)] to-[var(--brand-strong)]">
        <div className="max-w-4xl mx-auto px-4 pt-12 pb-24 text-center">
          <p className="text-lg font-semibold text-white tracking-wide">{BRAND_NAME}</p>
          <p className="text-sm text-white/80 mt-1.5 tracking-wide">{BRAND_SLOGAN}</p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 -mt-16">{children}</div>
    </div>
  );
}

function thClass(col: ShareColumn): string {
  return `px-3 py-2.5 text-xs font-medium text-gray-500 whitespace-nowrap bg-gray-50/80 border-b border-gray-100 ${
    col.numeric ? 'text-right' : 'text-left'
  }`;
}

/**
 * 单元格：金额右对齐 + 等宽数字（对数时眼睛是顺着列往下扫的，个位不对齐就扫不快）。
 * 余款非零补一档橙色——这一页的最终用途就是看还差多少钱。两节的余款列都按 `totalKey` 认，
 * 所以支出节那格改叫「待付余款」也照样是橙色。
 */
function tdClass(col: ShareColumn, cell: string): string {
  const base = 'px-3 py-2.5 border-b border-gray-50 text-gray-700';
  if (WRAPPING_HEADERS.has(col.header)) {
    return `${base} max-w-[15rem] whitespace-normal break-words leading-relaxed`;
  }
  if (!col.numeric) {
    // 订单号是这行的身份标识，用等宽灰字；其余按正文
    return col.header === '订单号'
      ? `${base} whitespace-nowrap font-mono text-gray-500`
      : `${base} whitespace-nowrap`;
  }
  const emphasis =
    col.totalKey === 'balance' && cell !== '0.00' ? 'text-amber-600 font-semibold' : 'font-medium text-gray-900';
  return `${base} whitespace-nowrap text-right tabular-nums ${emphasis}`;
}

function totalOf(totals: ShareTotals, key: keyof ShareTotals['values']): string {
  return `¥${totals.values[key]}`;
}

/**
 * 这一节的余款列叫什么，由载荷自己说：收入节是「待收余款」、支出节是「待付余款」（2026-10-11 用户裁定），
 * 页面不另抄一份方向→用词的映射，两边不会各叫各的。
 */
function balanceHeader(table: ShareTable): string {
  return table.columns.find((col) => col.totalKey === 'balance')?.header ?? '余款';
}

function OrderTable({ table }: { table: ShareTable }) {
  const style = SECTION_STYLES[table.direction];
  return (
    <div className={`${CARD_CLASS} overflow-hidden`}>
      <div className="px-5 py-3.5 border-b border-gray-100">
        <div className="flex items-center justify-between gap-3">
          <p className="flex items-center gap-2 min-w-0">
            <span className={`w-1.5 h-4 rounded-full ${style.bar} shrink-0`} />
            <span className={`text-sm font-semibold ${style.text} whitespace-nowrap`}>{table.label}</span>
            <span className="text-xs text-gray-400 whitespace-nowrap">{table.totals.count} 条</span>
          </p>
          <p className="text-xs text-gray-500 whitespace-nowrap">
            {balanceHeader(table)}合计{' '}
            <span className="text-sm font-semibold text-amber-600 tabular-nums">
              {totalOf(table.totals, 'balance')}
            </span>
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr>
              {table.columns.map((col) => (
                <th key={col.header} className={thClass(col)} scope="col">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => {
                  const col = table.columns[colIndex];
                  // 列数与表头同长是服务端载荷的不变式（有用例锁着），这里按位取列定义即可
                  return col ? (
                    <td key={col.header} className={tdClass(col, cell)}>
                      {cell}
                    </td>
                  ) : null;
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-[var(--brand-page)]">
              {table.columns.map((col, index) => (
                <td
                  key={col.header}
                  className={`px-3 py-2.5 text-xs whitespace-nowrap ${
                    index === 0
                      ? 'font-medium text-gray-600'
                      : col.totalKey
                        ? 'text-right font-semibold text-gray-900 tabular-nums'
                        : 'text-right text-gray-300'
                  }`}
                >
                  {index === 0 ? `合计 ${table.totals.count} 条` : col.totalKey ? totalOf(table.totals, col.totalKey) : ''}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="px-5 py-2 text-[11px] text-gray-400 sm:hidden">表格可左右滑动查看全部列</p>
    </div>
  );
}

/** 客户块：独立于表格。手机号是刻意带上的——重名客户只能靠它分清，而表里没有「联系方式」列 */
function CustomerBlock({ customer }: { customer: { fullName: string; companyName: string; phone: string } }) {
  return (
    <div className={`${CARD_CLASS} px-5 py-4`}>
      <p className="text-base font-semibold text-gray-900 break-all">{customer.fullName || '客户订单核对单'}</p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
        {customer.companyName && <p className="text-xs text-gray-500 break-all">{customer.companyName}</p>}
        {customer.phone && (
          <p className="text-xs text-gray-500 font-mono flex items-center gap-1">
            <i className="ri-phone-line" aria-hidden="true" />
            {customer.phone}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * 顶部读数：先给结论——这批单一共几张、两个方向各还差多少钱，再给明细表。
 *
 * 只有条数可以跨方向相加。收入那一节是「客户该付给分享方」、支出那一节是「分享方该付给客户」，
 * 把两个方向的余款加起来得到的数谁也不欠（用户 2026-10-11 判的错误计算），所以一节一格、
 * 缺哪一节就少一格（用户裁定：某一方向没有欠款时整项不显示），`订单金额 / 已付金额` 的分节读数在每张表的表尾。
 */
function TotalsStrip({ tables }: { tables: ShareTable[] }) {
  const cells: Array<{ label: string; value: string; tone: string }> = [
    {
      label: '订单条数',
      value: `${tables.reduce((sum, table) => sum + table.totals.count, 0)} 条`,
      tone: 'text-gray-900',
    },
    ...tables.map((table) => ({
      label: `${balanceHeader(table)}合计`,
      value: totalOf(table.totals, 'balance'),
      tone: table.direction === 'income' ? 'text-amber-600' : 'text-rose-500',
    })),
  ];
  return (
    <div className={`grid gap-2.5 grid-cols-2 ${cells.length >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
      {cells.map((cell) => (
        <div key={cell.label} className={`${CARD_CLASS} px-4 py-3`}>
          <p className="text-xs text-gray-400 whitespace-nowrap">{cell.label}</p>
          <p className={`text-base font-semibold mt-1 tabular-nums ${cell.tone}`}>{cell.value}</p>
        </div>
      ))}
    </div>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return <div className={`${CARD_CLASS} px-5 py-8 text-center`}>{children}</div>;
}

export default function SharedCustomerPage() {
  const { token } = useParams<{ token: string }>();
  const { data, isPending, error, isFetching } = useCustomerShareView(token ?? '');

  if (isPending) {
    return (
      <PageShell>
        <Panel>
          <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
            正在加载订单核对单…
          </p>
        </Panel>
      </PageShell>
    );
  }

  if (!data) {
    return (
      <PageShell>
        <div className={`${CARD_CLASS} p-8 text-center`}>
          <div className="w-12 h-12 mx-auto flex items-center justify-center bg-gray-100 rounded-full mb-4">
            <i className="ri-link-m text-gray-400 text-xl" aria-hidden="true" />
          </div>
          <p className="text-sm font-medium text-gray-900 mb-1.5">链接无法打开</p>
          <p className="text-sm text-gray-500 leading-relaxed">
            {error instanceof Error && error.message
              ? error.message
              : '分享链接无效或已过期，请联系分享者重新生成'}
          </p>
        </div>
      </PageShell>
    );
  }

  const view = data.view;

  return (
    <PageShell>
      <div className="space-y-3">
        <CustomerBlock customer={view.customer} />

        {/* 重新取数（超过 60s 的 staleTime）时旧数据还在：整页骨架会闪，只把明细区压暗 */}
        <div className={`space-y-3 transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
          <TotalsStrip tables={view.tables} />

          {view.tables.length === 0 ? (
            <Panel>
              <p className="text-sm text-gray-500">这位客户当前没有待结清的订单</p>
            </Panel>
          ) : (
            view.tables.map((table) => <OrderTable key={table.label} table={table} />)
          )}
        </div>
      </div>

      <div className="text-xs text-center text-gray-500 mt-5 leading-relaxed space-y-1">
        <p>
          本页不存快照：{new Date(view.generatedAt).toLocaleString('zh-CN')}
          是打开这条链接的那一刻，数据按分享者账户可见的已通过订单实时取
        </p>
        <p>链接于 {new Date(data.expiresAt).toLocaleString('zh-CN')} 后失效</p>
      </div>
    </PageShell>
  );
}
